import { ReplacementSourceEnum } from '@replacements/utils/floatingReplacementsToolbarUtils';
import { captureExceptionWithState } from 'util/sentry';
import { insertPauseTags } from '../extensions/PauseCueCommandsExtension';
import { objectToXml, XmlNode, xmlToObject } from './editorXml';

export const STABLE_REPLACEMENT_TAG_NAME = 'replacement';

/** Legacy respell markup, still present on takes authored before the stable schema. */
export const LEGACY_RESPELL_TAG_NAME = 'respell';

export type ReplacementType = 'respell' | 'word' | 'double_colon';

export type ReplacementSource = `${ReplacementSourceEnum}`;

export interface StableReplacementAttrs {
  value: string;
  type: ReplacementType;
  source?: ReplacementSource;
  originalText: string;
  entryId?: string;
}

export const EXAMPLE_STABLE_EDITOR_STATE = `Welcome to <replacement value="IH-t" type="respell" source="us-suggestion">it</replacement>'s world.

<tempo value="1.2">
  <loudness value="0">
    Say <replacement value="PEHR-suh-nuhl" type="respell" source="team">personal_a</replacement>
    or <replacement value="WELL-said" type="word" source="personal">WellSaid</replacement>.
  </loudness>
</tempo>

The CEO of <replacement value="N-Vidia" type="respell" source="llm-suggestion">NVIDIA</replacement> called.`;

export const replacementTypeFromIsRespelling = (
  isRespelling: boolean
): ReplacementType => (isRespelling ? 'respell' : 'word');

export const isRespellingFromType = (type: ReplacementType): boolean =>
  type !== 'word';

export function stripStableReplacements(formattedText: string): string {
  const pattern = new RegExp(
    `<${STABLE_REPLACEMENT_TAG_NAME}[^>]*>([\\s\\S]*?)<\\/${STABLE_REPLACEMENT_TAG_NAME}>`,
    'g'
  );

  return formattedText.replace(pattern, '$1');
}

const STABLE_REPLACEMENT_DETECTION_PATTERN = /<\/replacement>|<replacement\b/;
const RESPELL_XML_DETECTION_PATTERN = /<\/respell>|<respell\b/;
const DOUBLE_COLON_REPLACEMENT_DETECTION_PATTERN =
  /::([a-pr-wy-zA-PR-WY-Z-]+)::/;

export type ReplacementDetection = {
  includesReplacements: boolean;
  includesStableReplacement: boolean;
  includesRespellXml: boolean;
  includesDoubleColon: boolean;
};

/** Detect replacement markup in formatted text or editor state. */
export function detectReplacementFromText(text: string): ReplacementDetection {
  const includesStableReplacement =
    STABLE_REPLACEMENT_DETECTION_PATTERN.test(text);
  const includesRespellXml = RESPELL_XML_DETECTION_PATTERN.test(text);
  const includesDoubleColon =
    DOUBLE_COLON_REPLACEMENT_DETECTION_PATTERN.test(text);

  return {
    includesReplacements:
      includesStableReplacement || includesRespellXml || includesDoubleColon,
    includesStableReplacement,
    includesRespellXml,
    includesDoubleColon,
  };
}

/**
 * True when the text has replacement markup, in either the stable `<replacement>`
 * schema or the legacy `<respell>` markup.
 *
 * Replacements already marked `enabled="false"` count: `removeReplacements` deletes
 * them along with the rest, so they are still something the user would lose.
 */
export function hasReplacements(formattedText: string): boolean {
  const { includesStableReplacement, includesRespellXml } =
    detectReplacementFromText(formattedText);

  return includesStableReplacement || includesRespellXml;
}

/** The original text a replacement was applied to - its text children, joined. */
const replacementChildrenToText = (children: XmlNode[]): string =>
  children
    .filter(
      (child): child is Extract<XmlNode, { type: 'text' }> =>
        child.type === 'text'
    )
    .map(child => child.value)
    .join('');

/**
 * Remove every replacement from `formattedText`, unwrapping each one to the original
 * text it was applied to. Covers both the stable `<replacement>` schema and legacy
 * `<respell>` markup, and leaves surrounding cue markup untouched.
 *
 * Used when swapping to an avatar that cannot render replacements - the markup is
 * deleted rather than kept in a disabled state.
 */
export function removeReplacements(formattedText: string): string {
  const { includesStableReplacement, includesRespellXml } =
    detectReplacementFromText(formattedText);

  if (!includesStableReplacement && !includesRespellXml) {
    return formattedText;
  }

  try {
    const transform = (nodes: XmlNode[]): XmlNode[] =>
      nodes.map(node => {
        if (node.type === 'text') {
          return node;
        }

        if (
          node.name === STABLE_REPLACEMENT_TAG_NAME ||
          node.name === LEGACY_RESPELL_TAG_NAME
        ) {
          // A replacement only ever wraps text, so it collapses to a single node
          return {
            type: 'text' as const,
            value: replacementChildrenToText(node.children),
          };
        }

        return { ...node, children: transform(node.children) };
      });

    return objectToXml(transform(xmlToObject(formattedText)));
  } catch (error) {
    captureExceptionWithState(
      error instanceof Error ? error : new Error('Invalid replacement.'),
      {
        type: 'removeReplacements',
        value: {
          error: `error: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
        },
      }
    );
    // Never corrupt the script - leave the text untouched when it cannot be parsed
    return formattedText;
  }
}

const transformReplacementNode = (
  node: Extract<XmlNode, { type: 'element' }>,
  replacementsFormat: 'replacements:double_colon' | 'replacements:xml',
  transform: (nodesList: XmlNode[]) => XmlNode[]
): XmlNode[] => {
  const {
    value = '',
    type: replacementType,
    enabled = 'true',
  } = node.attributes;
  const originalText = replacementChildrenToText(node.children);

  if (enabled === 'false') {
    return [{ type: 'text', value: originalText }];
  }

  if (replacementsFormat === 'replacements:double_colon') {
    if (replacementType === 'double_colon' || replacementType === 'respell') {
      return [{ type: 'text', value: `::${value}::` }];
    }
    if (replacementType === 'word') {
      return [{ type: 'text', value }];
    }
  }

  if (replacementsFormat === 'replacements:xml') {
    if (replacementType === 'respell') {
      return [
        {
          type: 'element',
          name: 'respell',
          attributes: { value },
          children: transform(node.children),
        },
      ];
    }
    if (replacementType === 'word') {
      return [{ type: 'text', value }];
    }
    if (replacementType === 'double_colon') {
      return [{ type: 'text', value: `::${value}::` }];
    }
  }

  return [{ type: 'text', value: originalText }];
};

export function toTtsFormattedText(
  editorState: string,
  replacementsFormat: string | null
): string {
  const replacementExists =
    detectReplacementFromText(editorState).includesReplacements;
  if (
    replacementsFormat !== 'replacements:double_colon' &&
    replacementsFormat !== 'replacements:xml' &&
    !replacementExists
  ) {
    return editorState;
  }

  try {
    const nodes = xmlToObject(editorState);

    const transform = (nodesList: XmlNode[]): XmlNode[] =>
      nodesList.flatMap(node => {
        if (node.type === 'text') {
          return [node];
        }

        if (node.name === STABLE_REPLACEMENT_TAG_NAME) {
          return transformReplacementNode(
            node,
            replacementsFormat as
              | 'replacements:double_colon'
              | 'replacements:xml',
            transform
          );
        }

        return [
          {
            ...node,
            children: transform(node.children),
          },
        ];
      });

    return objectToXml(transform(nodes));
  } catch (error) {
    captureExceptionWithState(
      error instanceof Error ? error : new Error('Invalid replacement.'),
      {
        type: 'toTtsFormattedText',
        value: {
          error: `error: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
        },
      }
    );
    throw new Error('Invalid replacement.');
  }
}

/**
 * Derive TTS egress from editor state.
 *
 * @todo [replacement-stability]: migrate this fully to the backend
 */
export function deriveFormattedTextForTts(
  formattedText: string,
  replacementsFormat: string | null
): string {
  return toTtsFormattedText(formattedText, replacementsFormat);
}

/*
 * Converts formattedText (newline-delimited cue markup) into the HTML
 * the editor's remirror document expects, wrapping lines in <p> tags
 * and collapsing tempo/loudness cues into pause tags.
 */
export function formattedTextToHtml(formattedText: string): string {
  const lines = (formattedText ?? '').split(/\r?\n/);
  const html = lines.map(line => `<p>${line ?? ''}</p>`).join('');
  return insertPauseTags(html);
}
